<template>
  <div class="flex h-screen overflow-hidden">
    <!-- 사이드바 -->
    <AppSidebar
      :token-usage="tokenUsage"
      :chat-history="chatHistory"
      :current-chat-id="currentChat?.id"
      :debug-mode="debugMode"
      :show-chat-features="true"
      :show-history-panel="showHistoryPanel"
      :playground-info-text="null"
      :bottom-button-text="showHistoryPanel ? '← View All' : '← Chat Page'"
      @new-chat="createNewChat"
      @select-chat="loadChat"
      @view-all="toggleHistoryPanel"
      @update:debug-mode="updateDebugMode"
      class="w-80 flex-shrink-0"
      :is-loading="isChatHistoryLoading"
    />
    <!-- View All(전체보기) 패널 -->
    <div v-if="showHistoryPanel" class="flex flex-col flex-1 min-h-0 bg-white overflow-y-auto h-screen" :class="{ 'pl-12': isSidebarHidden }">
      <!-- View All 상단 네비게이션 (고정) -->
      <div class="flex items-center justify-between p-4 bg-white relative z-[100] flex-shrink-0" :class="{ 'pl-12': isSidebarHidden }">
        <div class="mx-auto flex items-center justify-between mr-0">
          <div class="flex items-center mr-4" :class="{ 'pl-10': isSidebarHidden }">
            <AppHamburgerMenu
              :debug-mode="debugMode"
              @toggle-debug="toggleDebugMode"
              @new-chat="createNewChat"
            />
          </div>
          <div class="flex items-center gap-4">
            <AppUserMenu
              :user-name="userName"
              :user-email="userEmail"
              :debug-mode="debugMode"
              @update:debug-mode="updateDebugMode"
              @logout="logout"
            />
          </div>
        </div>
      </div>
      <!-- 아래만 스크롤 -->
      <div class="flex-1 min-h-0 overflow-y-auto">
        <div class="w-full max-w-5xl mx-auto">
          <ChatHistoryViewAll
            ref="chatHistoryViewAll1"
            :chats="chatHistory"
            @select-chat="loadChat"
            @delete-selected="deleteSelectedChats"
            :is-loading="isChatHistoryLoading"
          />
        </div>
      </div>
    </div>
    <!-- 실제 채팅 UI -->
    <div v-else class="flex-1 flex flex-col min-h-0 pl-0 z-0 relative" :class="{ 'pl-2': isSidebarHidden }">
      <!-- 상단 네비게이션 -->
      <div class="flex items-center justify-between p-4 bg-white relative z-[100]">
        <div class="flex items-center" :class="{ 'pl-14': isSidebarHidden }">
          <button
            v-if="isSidebarHidden"
            @click="toggleSidebar"
            title="사이드바 열기"
          >
            <img src="@/assets/icons/bar_side.png" alt="사이드바 열기" class="h-6 w-6" />
          </button>
          <div class="w-[400px]" v-if="!showHistoryPanel">
            <AppModelSelect
              :selected-model="selectedModel"
              :disabled="isModelSelectionDisabled"
              :placeholder="modelPlaceholder"
              :reset-on-new-chat="true"
              @select-model="selectModel"
            />
          </div>
          <!-- <span
            v-if="shouldShowDeletionNotice && !showHistoryPanel"
            class="absolute left-1/2 top-1/2 -translate-y-1/2 text-red-600 text-sm font-medium whitespace-nowrap"
            :style="deletionNoticeStyle"
          >
            7일 후 삭제될 히스토리 입니다.
          </span> -->
        </div>
        <div class="flex items-center gap-4">
          <AppHamburgerMenu
            :debug-mode="debugMode"
            @toggle-debug="toggleDebugMode"
            @new-chat="createNewChat"
          />
          <AppUserMenu
            :user-name="userName"
            :user-email="userEmail"
            :debug-mode="debugMode"
            @update:debug-mode="updateDebugMode"
            @logout="logout"
          />
        </div>
      </div>
      <!-- 채팅 컨테이너 -->
      <div class="flex flex-col flex-1 min-h-0">
        <template v-if="showHistoryPanel">
         <div :class="{ 'pl-12': isSidebarHidden }">
          <ChatHistoryViewAll
            ref="chatHistoryViewAll2"
            :chats="chatHistory"
            @select-chat="loadChat"
            @delete-selected="deleteSelectedChats"
            :is-loading="isChatHistoryLoading"
          />
         </div>
        </template>
        <template v-else>
          <!-- 메시지 영역 -->
          <div class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 bg-white" ref="chatContainer" :class="{ 'pl-12': isSidebarHidden }">
            <div v-if="messages.length === 0" class="flex flex-col items-center justify-center h-full">
              <div class="flex flex-col items-center gap-6 w-full max-w-2xl px-4">
                <h1 class="text-3xl font-bold text-gray-700" title="새로운 대화 시작">무엇을 도와드릴까요?</h1>
                <ChatInput
                  ref="chatInputEmpty"
                  @send-message="handleSendMessage"
                  @pause="handlePause"
                  :messages="messages"
                  :is-typing="isTyping"
                  :min-height="120"
                  :key="currentChat?.id"
                  class="w-full"
                  @input-resize="handleInputResize"
                />
              </div>
            </div>
            <div v-else class="max-w-5xl mx-auto">
              <ChatMessage
                ref="chatMessageComponent"
                :messages="messages"
                :debug-mode="debugMode"
                :is-waiting-response="isWaitingResponse"
                :selected-model="selectedModel"
                :current-chat="currentChat"
                @message-success="handleMessageSuccess"
                @edit-message="handleEditMessage"
                @remove-loading-message="handleRemoveLoadingMessage"
                @add-loading-message="handleAddLoadingMessage"
                @update-user-message="handleUpdateUserMessage"
              />
            </div>
          </div>
          <!-- 입력창 (실제 채팅) -->
          <div v-if="messages.length > 0" class="w-full flex justify-center" :class="{ 'pl-12': isSidebarHidden }">
            <div style="width: 100%; margin: 0 auto 18px auto; padding: 0 16px;">
              <div class="flex flex-col gap-4">
                <ChatMessage
                  ref="chatMessageComponentMain"
                  :messages="messages"
                  :debug-mode="debugMode"
                  :is-waiting-response="isWaitingResponse"
                  :selected-model="selectedModel"
                  :current-chat="currentChat"
                  @message-success="handleMessageSuccess"
                  @edit-message="handleEditMessage"
                  @remove-loading-message="handleRemoveLoadingMessage"
                  @add-loading-message="handleAddLoadingMessage"
                  @update-user-message="handleUpdateUserMessage"
                  style="display: none;"
                />
              <ChatInput
                ref="chatInputMain"
                  @send-message="handleSendMessage"
                @pause="handlePause"
                  :messages="messages"
                  :is-typing="isTyping"
                :min-height="48"
                :key="currentChat?.id"
                class="w-full"
                @input-resize="handleInputResize"
              />
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
    <AlertModal
      :visible="showAlertModal"
      :message="alertMessage"
      :type="alertType"
      @confirm="handleAlertConfirm"
      @cancel="handleAlertCancel"
    />
    <!-- PII 마스킹 다이얼로그 (인라인 체크박스 UI) -->
    <AlertModal
      v-if="showMaskingModal"
      :visible="showMaskingModal"
      :message="''"
      size="wide"
    >
      <template #default>
        <div class="mb-4 mt-6">
          <div class="flex items-center mb-2">
            <img src="@/assets/icons/masking.png" alt="lock" class="w-5 h-5 mr-2" />
            <span class="text-gray-800 font-semibold text-lg">개인정보 보호</span>
          </div>
          <div class="text-gray-600 text-left pl-2">
            &bull;&nbsp;개인정보가 감지되어 자동으로 마스킹되었습니다. 원본 정보를 보내려면 체크를 해제하세요.
          </div>
        </div>
        <div class="w-full bg-gray-50 border border-gray-200 p-4 mt-4 mb-4 rounded-lg min-h-[48px] text-gray-800 text-sm font-medium text-left" style="white-space:pre-wrap;word-break:break-all;line-height:1.6;">
          <span
            v-for="(part, i) in getMaskedParts()"
            :key="'part-'+i"
            class="mr-1 align-middle"
          >
            <template v-if="part.type === 'pii'">
              <span class="inline-flex items-center space-x-1">
                <input
                  type="checkbox"
                  v-model="maskingState.masked[part.idx]"
                  @change="updateMasked"
                  class="masking-checkbox"
                  tabindex="-1"
                />
                <span class="inline-block break-all bg-amber-100 text-amber-800 px-2 py-1 rounded-md border border-amber-200 font-bold">
                  {{ getMaskedText(part, maskingState.masked[part.idx]) }}
                </span>
              </span>
            </template>
            <template v-else>
              <span>{{ part.text }}</span>
            </template>
          </span>
        </div>
        <div class="flex justify-center gap-3 mt-6 mb-2">
          <button
            class="px-16 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-300 min-w-[100px] transition-colors"
            @click="handleMaskingCancel"
            :disabled="maskingLoading"
            title="취소"
          >
            취소
          </button>
          <button
            class="px-16 py-2 bg-blue-200 text-white rounded-lg hover:bg-blue-500 min-w-[100px] transition-colors"
            @click="handleMaskingConfirm"
            :disabled="maskingLoading"
            title="전송"
          >
            {{ maskingLoading ? '전송 중...' : '전송' }}
          </button>
        </div>
      </template>
    </AlertModal>
  </div>
</template>

<script>
import AppSidebar from '@/components/navigation/Sidebar.vue'
import AppHamburgerMenu from '@/components/navigation/HamburgerMenu.vue'
import AppUserMenu from '@/components/navigation/UserMenu.vue'
import AppModelSelect from '@/components/navigation/ModelSelect.vue'
import ChatInput from '@/components/chat/ChatInput.vue'
import ChatHistoryViewAll from '@/components/chat/ChatHistoryViewAll.vue'
import ChatMessage from '@/components/chat/ChatMessage.vue'
import AlertModal from '@/components/common/AlertModal.vue'
import { chatAPI, secureChatAPI, authAPI, deleteChatLogs, normalizeChatResponse, fetchSessionSummaries, fetchSessionMessages } from '@/utils/api'
import { handleApiError } from '@/utils/apiErrorHandler';
import { processRequest } from '@/utils/message/messagePipeline'
import { mapState, mapActions } from 'vuex'
import { encryptMessage } from '@/utils/message/encrypt'
import { getAllSupportedModels, findModelById, getDefaultModel } from '@/utils/models'
import debounce from 'lodash/debounce'

// UUID 형식 판별 함수 (서버에 저장된 메시지 id만 삭제)
function isUUID(str) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
}

export default {
  name: 'ChatView',
  components: {
    AppSidebar,
    AppHamburgerMenu,
    AppUserMenu,
    AppModelSelect,
    ChatInput,
    ChatHistoryViewAll,
    ChatMessage,
    AlertModal
  },
  data() {
    return {
      showHistoryPanel: this.$route.query.viewAll === '1',
      currentChat: null,
      messages: [],
      prompt: '',
      chatHistory: [],
      searchQuery: '',
      selectedChats: [],
      tokenUsage: {
        percentage: 0
      },
      debugMode: false,
      isPaused: false,
      currentAbortController: null, // SDK v1.2.1: 스트림 중단용 AbortController
      isApiCalling: false, // API 호출 중 상태 추가
      isStreaming: false,  // SDK v1.2.1: 실제 스트리밍 상태
      isAborting: false,   // SDK v1.2.1: 스트림 중단 중 상태
      // DEFAULT_MODEL_ID를 기본 모델로 사용
      ...(() => {
        const _def = getDefaultModel();
        return {
          selectedModel: { id: _def.id, name: _def.name },
          modelPlaceholder: _def.name,
        };
      })(),
      userName: '',
      userEmail: '',
      userId: '',
      isWaitingResponse: false,
      isTyping: false,
      currentResponseController: null,
      error: null,
      inputAreaHeight: 120, // 입력창 높이 기본값(새 채팅)
      wasAtBottom: true, // 입력창 크기 변화 전 스크롤 상태 저장
      prevScrollHeight: 0,
      prevScrollTop: 0,
      prevClientHeight: 0,
      showAlertModal: false,
      alertMessage: '',
      alertType: 'alert',
      alertCallback: null,
      isChatHistoryLoading: true,
      // PII 확인 다이얼로그 (simple)
      showPiiConfirmModal: false,
      piiConfirmData: null,
      // 인라인 마스킹 모달 (ChatMessage.vue 패턴)
      showMaskingModal: false,
      maskingState: null,
      maskingParts: [],
      maskingUserMessage: null,
      maskingLoading: false,
    }
  },
  created() {
    // 디버그 모드 복원 (localStorage)
    const storedDebug = localStorage.getItem('debugMode');
    if (storedDebug !== null) {
      this.debugMode = storedDebug === 'true';
    }
    window.addEventListener('resize', this.handleResize)
    this.handleResize()
    if (this.$route.query.new === '1') {
      this.createNewChat();
      this.$router.replace({ path: '/chat' });
      return;
    }
    if (this.messages.length === 0) {
      this.isModelSelectionDisabled = false;
    }
    // 사용자 정보 세팅 (View All 분기 전에 반드시 실행)
    const userInfo = JSON.parse(localStorage.getItem('userInfo'))
    if (userInfo) {
      this.userName = userInfo.name || ''
      this.userEmail = userInfo.email || ''
      this.userId = userInfo.userId || userInfo.email || ''
    } else {
      this.$router.push('/login')
      return
    }
    // 쿼리스트링에 따라 View All 상태 초기화
    if (this.$route.query.viewAll === '1') {
      this.showHistoryPanel = true;
      // View All 상태에서도 히스토리 반드시 로드
      // this.loadChatHistory(); // [중복 호출 방지] mounted()에서만 호출하도록 주석 처리
      return;
    }
    // 이하 기존 채팅방 자동 진입/초기화 로직 (예: 마지막 채팅방 복원, 새 채팅 생성 등)
    // ... (기존 mounted/created의 채팅방 진입/초기화 로직이 여기에 위치) ...
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('force-new-chat', this.createNewChat)
  },
  computed: {
    ...mapState(['sidebarVisible']),
    isSidebarHidden() {
      return !this.sidebarVisible;
    },
    filteredChats() {
      if (!this.searchQuery) return this.chatHistory
      const query = this.searchQuery.toLowerCase()
      return this.chatHistory.filter(chat =>
        chat.title.toLowerCase().includes(query)
      )
    },
    // 현재 채팅방이 30일 보관 기간 중 삭제까지 7일 이하로 남았는지 여부
    shouldShowDeletionNotice () {
      if (!this.currentChat || !this.currentChat.created_at_ms) return false
      const now = Date.now()
      const diffMs = now - this.currentChat.created_at_ms
      const daysSinceCreation = diffMs / 1000 / 60 / 60 / 24
      // 30일 - 7일 = 23일 → 23~30일 범위에 포함되면 안내 표시
      return daysSinceCreation >= 1 && daysSinceCreation < 30
    },
    deletionNoticeStyle() {
      // 사이드바 확장(320px) ↔ 숨김(48px) 차이 = 272px, 절반 = 136px 보정
      const offset = this.isSidebarHidden ? 18 : 0;
      // px 단위는 Tailwind padding(24px) + 로고 영역 실제 시각적 오프셋 등을 고려해 조정 가능
      return {
        transform: `translate(calc(-50% + ${offset}px), -50%)`
      };
    }
  },
  methods: {
    ...mapActions(['toggleSidebar', 'setSidebarVisible']),
    // 배열을 지정된 크기의 배치로 나누는 유틸리티 함수
    chunkArray(array, chunkSize) {
      const chunks = [];
      for (let i = 0; i < array.length; i += chunkSize) {
        chunks.push(array.slice(i, i + chunkSize));
      }
      return chunks;
    },
    handleResize() {
      // 필요시 반응형 처리
    },
    openSidebar() {
      this.setSidebarVisible(true);
    },
    closeSidebar() {
      this.setSidebarVisible(false);
    },
    toggleSidebar() {
      this.toggleSidebar();
    },
    async createNewChat() {
      // 응답 중이면 중지 버튼과 동일한 효과
      if (this.isWaitingResponse || this.isTyping || this.isApiCalling) {
        this.handlePause();
      }
      
      // View All 상태라면 먼저 쿼리스트링 제거 및 showHistoryPanel=false로 전환 후 새 채팅 생성 로직 실행
      if (this.$route.query.viewAll === '1' || this.showHistoryPanel) {
        await this.$router.push({ path: '/chat' });
        this.showHistoryPanel = false;
        // 이후 새 채팅 생성 로직 계속 진행
      }
      try {
        this.isModelSelectionDisabled = false; // 새 채팅 시 선택 가능
        const model = getDefaultModel();
        this.selectedModel = model;
        this.modelPlaceholder = model.name;
        this.showHistoryPanel = false;
        // 현재 채팅이 새 채팅인지 확인
        if (this.currentChat?.title === '새 대화' && this.messages.length === 0) {
          return;
        }
        // 디버그 모드 OFF
        // this.debugMode = false; // 유지: 사용자 설정 존중
        // sessionId 생성
        const sessionId = crypto.randomUUID();
        localStorage.setItem('lastSessionId', sessionId);
        // 임시 채팅 객체 생성 (임시 상태지만 chatHistory에는 추가하지 않음)
        const tempChat = {
          id: sessionId,
          sessionId: sessionId,
          title: '새 대화',
          created_at_ms: Date.now(),
          created_at: new Date().toISOString(),
          messages: [],
          model: model.id,
          isTemp: true
        };
        this.currentChat = tempChat;
        this.messages = [];
        this.showHistoryPanel = false;
        // chatHistory에는 추가하지 않음 (첫 메시지 전송 시에만 추가)
        if (this.$route.path !== '/chat') {
          await this.$router.push('/chat');
        }

        // 새 채팅 생성 후 입력창에 포커스
        this.$nextTick(() => {
          this.focusMessageInput();
        });
      } catch (error) {
        console.error('새 채팅 생성 중 오류 발생:', error);
      }
    },
    /**
     * predictions가 있는 메시지마다 안내문구(system)를 추가하는 함수
     * - messages 배열 전체를 순회하며 predictions가 있는 메시지 바로 아래에 안내문구(system)를 삽입
     * - 이미 안내문구가 있으면 중복 삽입하지 않음
     * @param {Array} messages - 채팅 메시지 배열
     * @returns {Array} 안내문구가 추가된 메시지 배열
     */
    ensureModerationWarnings(messages) {
      let result = [...messages];
      messages.forEach((msg, idx) => {
        if (
          Array.isArray(msg.predictions) &&
          msg.predictions.length > 0 &&
          !result.some(
            m =>
              m.role === 'system' &&
              m.content === '입력하신 내용 및 답변에 유해 내용이 포함되어 있습니다.' &&
              m.timestamp > msg.timestamp
          )
        ) {
          result.splice(
            idx + 1,
            0,
            {
              id: 'moderation-warning-' + Date.now() + '-' + idx,
              role: 'system',
              content: '입력하신 내용 및 답변에 유해 내용이 포함되어 있습니다.',
              timestamp: Date.now(),
              modelName: msg.modelName || ''
            }
          );
        }
      });
      return result;
    },
    /**
     * 채팅방 로드 및 전환을 처리하는 메서드
     * - 채팅 히스토리에서 선택된 세션 로드
     * - 메시지 목록 갱신
     * - 화면 상태 업데이트
     */
    async loadChat(sessionId) {
      console.log('[loadChat] 진입, 현재 상태:', {
        sessionId,
        isWaitingResponse: this.isWaitingResponse,
        isTyping: this.isTyping,
        currentSessionId: this.currentChat?.sessionId
      });
      
      // 응답 중이면 중지
      if (this.isWaitingResponse || this.isTyping || this.isApiCalling) {
        console.log('[loadChat] 응답 중이므로 handlePause() 호출');
        this.handlePause();
      }
      
      // View All 상태 체크
      const cameFromViewAll = (this.$route.query.viewAll === '1' || this.showHistoryPanel);
      
      try {
        // 동일한 채팅방 클릭 시 처리
        if (this.currentChat?.sessionId === sessionId) {
          if (!cameFromViewAll) {
            this.showHistoryPanel = false;
          }
          return;
        }

        // 마지막 세션 ID 저장
        localStorage.setItem('lastSessionId', sessionId);
        
        // 채팅방 정보 찾기
        const chat = this.chatHistory.find(c => c.sessionId === sessionId);
        if (!chat) {
          console.error('[loadChat] 채팅방을 찾을 수 없음:', sessionId);
          this.showAlertModal = true;
          this.alertMessage = '채팅방을 찾을 수 없습니다.';
          this.alertType = 'error';
          return;
        }
        
        this.currentChat = chat;
        
        try {
          // 항상 최신 메시지를 로드
          console.log('[loadChat] 최신 메시지 로드 시작:', sessionId);
          const fullItems = await fetchSessionMessages(sessionId);
          console.log('[loadChat] 서버에서 가져온 세션 메시지:', fullItems);
          
          // 메시지 매핑
          chat.messages = (fullItems || []).map(item => ({
            id: item.eventId || item.id || Math.random().toString(36).slice(2,8),
            role: item.senderRole === 'USER' ? 'user' : 'assistant',
            content: item.content || '',
            timestamp: item.timestamp,
            metadata: item.attachments && item.attachments.length > 0 ? {
              attachments: item.attachments.map(att => ({
                id: att.id,
                name: att.name,
                size: att.size,
                mime_type: att.mime_type || 'application/pdf'
              }))
            } : undefined,
            reference_docs: (item.refResources || item.reference_docs || []).map(r => ({
              content: r.chunkContent || r.content || '',
              metadata: {
                file_name: r.name || r.metadata?.file_name || '알 수 없는 파일',
                distance: r.distance || r.metadata?.distance || 0,
                source_path: r.metadata?.source_path || '',
                id: r.id || r.metadata?.id
              }
            }))
          }));
          
          // 모델 정보 설정
          if (!chat.model && fullItems && fullItems.length > 0) {
            const firstMessage = fullItems[0];
            if (firstMessage && firstMessage.model) {
              chat.model = firstMessage.model;
              console.log(`[loadChat] sessionId ${sessionId}의 model 정보를 메시지에서 추출: ${firstMessage.model}`);
            }
          }
        } catch (e) {
          console.error('세션 메시지 조회 실패:', e);
          this.showAlertModal = true;
          this.alertMessage = '메시지를 불러오는데 실패했습니다.';
          this.alertType = 'error';
          return;
        }

        // 모델 정보 설정
        if (chat.model) {
          const modelList = getAllSupportedModels();
          const modelObj = modelList.find(m => m.id === chat.model) || { id: chat.model, name: chat.model };
          this.selectedModel = modelObj;
          this.modelPlaceholder = modelObj.name;
          this.isModelSelectionDisabled = true;
        } else {
          const defaultModel = getDefaultModel();
          this.selectedModel = { id: defaultModel.id, name: defaultModel.name };
          this.modelPlaceholder = defaultModel.name;
          this.isModelSelectionDisabled = true;
        }

        // 메시지 처리 및 UI 업데이트
        this.messages = (chat.messages || []).map(msg => ({
          ...msg,
          reference_docs: msg.reference_docs || (msg.refResources || []).map(r => ({
            content: r.chunkContent || r.content || '',
            metadata: {
              file_name: r.name || r.metadata?.file_name || '알 수 없는 파일',
              distance: r.distance || r.metadata?.distance || 0,
              source_path: r.metadata?.source_path || '',
              id: r.id || r.metadata?.id
            }
          })),
          modelName: this.selectedModel?.name || this.selectedModel?.id || ''
        }));

        // 임시 메시지 처리
        const tempMsgs = this.getTempAssistantMessages(sessionId);
        const serverMsgIds = new Set(this.messages.map(m => m.id));
        const serverMsgContents = new Set(this.messages.filter(m => m.role === 'assistant').map(m => (m.content || '').trim()));
        
        const filteredTempMsgs = tempMsgs.filter(m => {
          if (serverMsgIds.has(m.id)) return false;
          if (m.role === 'assistant' && m.content && serverMsgContents.has(m.content.trim())) return false;
          return true;
        });
        
        if (filteredTempMsgs.length > 0) {
          this.messages = [...this.messages, ...filteredTempMsgs];
        }

        // 임시 메시지 정리
        this.removeTempAssistantMessages(sessionId, this.messages);
        this.messages = this.ensureModerationWarnings(this.messages);
        this.isModelSelectionDisabled = (this.messages.length > 0);
        
        // UI 상태 업데이트
        this.showHistoryPanel = false;
        
        // 라우터 처리
        if (cameFromViewAll) {
          await this.$router.push({ path: '/chat', query: {} });
        } else if (this.$route.path !== '/chat') {
          await this.$router.push('/chat');
        }

        // 메시지 암호화 및 스크롤
        await this.encryptVisibleMessages();
        this.$nextTick(() => {
          this.scrollToBottom();
          this.focusMessageInput();
        });

      } catch (error) {
        console.error('채팅 로드 중 오류 발생:', error);
        this.showAlertModal = true;
        this.alertMessage = '채팅을 불러오는데 실패했습니다.';
        this.alertType = 'error';
      }
    },
    loadChatAndCloseHistory(chatId) {
      this.loadChat(chatId);
    },
    /**
     * 선택된 채팅방들을 일괄 삭제하는 메서드
     * - 서버 API 호출하여 채팅 로그 삭제
     * - 로컬 상태 업데이트
     * - 삭제 후 적절한 화면 전환
     */
    async deleteSelectedChats(selectedSessionIds) {
      try {
        // 선택된 세션 중 아직 메시지를 로드하지 않은 경우 fetchSessionMessages 로 동기 로딩
        for (const chat of this.chatHistory.filter(chat => selectedSessionIds.includes(chat.id))) {
          if (!chat.messages || chat.messages.length === 0) {
            try {
              const msgs = await fetchSessionMessages(chat.id);
              if (Array.isArray(msgs)) {
                if (this.$set) {
                  this.$set(chat, 'messages', msgs);
                } else {
                  chat.messages = msgs;
                }
              }
            } catch (e) {
              console.error('[Chat.vue] 세션 삭제 전 메시지 로딩 실패', e);
            }
          }
        }
        
        const requestIds = this.chatHistory
          .filter(chat => selectedSessionIds.includes(chat.id))
          .flatMap(chat => (chat.messages || [])
            .map(msg => msg.requestId)
          )
          .filter(isUUID)
          .filter((id, idx, arr) => id && arr.indexOf(id) === idx); // 중복 제거
        
        if (requestIds.length > 0) {
          // requestIds를 10개씩 배치로 나누어 순차 처리
          const batchSize = 10;
          const batches = this.chunkArray(requestIds, batchSize);
          let successCount = 0;
          let failCount = 0;
          
          console.log(`[deleteSelectedChats] 총 ${requestIds.length}개 항목을 ${batches.length}개 배치로 나누어 삭제 시작`);
          
          for (let i = 0; i < batches.length; i++) {
            const batch = batches[i];
            try {
              console.log(`[deleteSelectedChats] 배치 ${i + 1}/${batches.length} 처리 중... (${batch.length}개)`);
              await deleteChatLogs(batch);
              successCount += batch.length;
              console.log(`[deleteSelectedChats] 배치 ${i + 1} 성공 (${batch.length}개)`);
            } catch (error) {
              console.error(`[deleteSelectedChats] 배치 ${i + 1} 실패:`, error);
              failCount += batch.length;
            }
          }
          
          // 결과 요약
          if (failCount > 0) {
            this.alertMessage = `삭제 완료: 성공 ${successCount}개, 실패 ${failCount}개`;
            this.showAlertModal = true;
          } else {
            console.log(`[deleteSelectedChats] 모든 삭제 완료: ${successCount}개`);
          }
        }
        
        // 선택 목록 초기화하여 삭제 버튼 비활성화 & 체크박스 해제
        if (Array.isArray(this.selectedSessionIds)) {
          this.selectedSessionIds.splice(0, this.selectedSessionIds.length);
        } else if (this.selectedSessionIds && typeof this.selectedSessionIds.clear === 'function') {
          this.selectedSessionIds.clear(); // Set 등
        } else {
          this.selectedSessionIds = [];
        }
        
        // 프론트 상태에서는 sessionId 기준으로 제거
        this.chatHistory = this.chatHistory.filter(
          chat => !selectedSessionIds.includes(chat.id)
        );
        
        // 현재 채팅이 삭제된 경우 처리
        if (selectedSessionIds.includes(this.currentChat?.id)) {
          // View All 상태라면 상태 유지하면서 currentChat만 초기화
          if (this.$route.query.viewAll === '1' || this.showHistoryPanel) {
            this.currentChat = null;
            this.messages = [];
            // View All 상태는 유지 (showHistoryPanel과 URL 쿼리스트링 그대로)
          } else {
            // View All 상태가 아니라면 새 채팅 생성
          await this.createNewChat();
        }
        }
        
        // ChatHistoryViewAll 컴포넌트에 삭제 완료 알림
        this.notifyDeleteCompleted();
      } catch (error) {
        console.error('채팅 삭제 중 오류 발생:', error);
        this.alertMessage = '채팅 삭제 중 오류가 발생했습니다.';
        this.showAlertModal = true;
      }
    },
    // ChatHistoryViewAll 컴포넌트에 삭제 완료 알림
    notifyDeleteCompleted() {
      // 두 개의 ChatHistoryViewAll ref 중 현재 활성화된 것을 찾아서 호출
      if (this.$refs.chatHistoryViewAll1) {
        this.$refs.chatHistoryViewAll1.onDeleteCompleted();
      }
      if (this.$refs.chatHistoryViewAll2) {
        this.$refs.chatHistoryViewAll2.onDeleteCompleted();
      }
    },
    handleAlertConfirm() {
      if (this.alertCallback) this.alertCallback(true);
      this.showAlertModal = false;
      this.alertCallback = null;
    },
    handleAlertCancel() {
      if (this.alertCallback) this.alertCallback(false);
      this.showAlertModal = false;
      this.alertCallback = null;
    },
    // PII 확인 다이얼로그 - 마스킹하여 전송
    async handlePiiConfirm() {
      this.showPiiConfirmModal = false;
      if (!this.piiConfirmData?.userMessage) return;

      const userMessage = this.piiConfirmData.userMessage;
      this.piiConfirmData = null;

      // overridePrivacyFilter: true로 재전송
      try {
        this.isApiCalling = true;

        // 로딩 메시지 추가
        const loadingMsg = {
          id: 'loading_' + Date.now(),
          role: 'loading',
          content: userMessage.content,
          modelName: this.selectedModel?.name || this.selectedModel?.id || '',
          timestamp: null
        };
        this.messages.push(loadingMsg);
        this.scrollToBottom();

        const payload = {
          model: this.selectedModel?.id || 'gemma3:4b',
          messages: [{ role: 'user', content: userMessage.content }],
          stream: true
        };

        this.currentAbortController = new AbortController();
        const serverResponse = await secureChatAPI.sendMessage(
          this.currentChat?.sessionId || `temp_${Date.now()}`,
          payload,
          {
            returnStream: true,
            overridePrivacyFilter: true,  // PII 필터 우회
            signal: this.currentAbortController.signal
          }
        );

        // 로딩 메시지 제거
        const loadingIdx = this.messages.findIndex(m => m.id === loadingMsg.id);
        if (loadingIdx !== -1) {
          this.messages.splice(loadingIdx, 1);
        }

        // 스트림 처리
        if (serverResponse instanceof ReadableStream || (serverResponse && typeof serverResponse.getReader === 'function')) {
          const assistantMsg = {
            id: 'assistant_' + Date.now(),
            role: 'assistant',
            content: '',
            modelName: this.selectedModel?.name || this.selectedModel?.id || '',
            timestamp: null,
            reference_docs: []
          };
          this.messages.push(assistantMsg);
          const assistantIdx = this.messages.length - 1;
          await this.processRealStream(serverResponse, assistantIdx, userMessage);
        } else {
          // 일반 응답 처리
          const normalized = normalizeChatResponse(serverResponse);
          const assistantMsg = {
            id: normalized.id || 'assistant_' + Date.now(),
            role: 'assistant',
            content: normalized.content,
            modelName: this.selectedModel?.name || this.selectedModel?.id || '',
            timestamp: Date.now(),
            reference_docs: normalized.reference_docs || []
          };
          this.messages.push(assistantMsg);
        }
      } catch (err) {
        console.error('[handlePiiConfirm] 재전송 실패:', err);
        this.alertMessage = '메시지 재전송에 실패했습니다.';
        this.showAlertModal = true;
      } finally {
        this.isApiCalling = false;
        this.isWaitingResponse = false;
        // 안전장치: 남아있는 로딩 메시지 제거
        const remainingLoading = this.messages.findIndex(m => m.role === 'loading');
        if (remainingLoading !== -1) {
          this.messages.splice(remainingLoading, 1);
        }
        this.scrollToBottom();
      }
    },
    // PII 확인 다이얼로그 - 취소 (simple modal - deprecated)
    handlePiiCancel() {
      this.showPiiConfirmModal = false;
      this.piiConfirmData = null;
    },
    // 인라인 마스킹 모달 - 전송 확인
    async handleMaskingConfirm() {
      const maskedContent = this.getMaskedUserContent();
      console.log('[handleMaskingConfirm] 마스킹된 콘텐츠:', maskedContent);

      if (!maskedContent || maskedContent.trim().length === 0) {
        this.alertMessage = '마스킹된 메시지가 비어 있습니다.';
        this.showAlertModal = true;
        return;
      }

      this.showMaskingModal = false;
      this.maskingLoading = true;

      const userMessage = this.maskingUserMessage;

      // ✅ 기존 사용자 메시지의 content를 마스킹된 내용으로 업데이트
      if (userMessage && userMessage.id) {
        const userMsgIdx = this.messages.findIndex(m => m.id === userMessage.id);
        if (userMsgIdx !== -1) {
          this.$set(this.messages, userMsgIdx, {
            ...this.messages[userMsgIdx],
            content: maskedContent
          });
          console.log('[handleMaskingConfirm] 사용자 메시지 업데이트 완료:', userMsgIdx);
        }
      }

      this.maskingUserMessage = null;
      this.maskingState = null;
      this.maskingParts = [];

      // overridePrivacyFilter: true로 재전송
      try {
        this.isApiCalling = true;

        // 로딩 메시지 추가
        const loadingMsg = {
          id: 'loading_' + Date.now(),
          role: 'loading',
          content: maskedContent,
          modelName: this.selectedModel?.name || this.selectedModel?.id || '',
          timestamp: null
        };
        this.messages.push(loadingMsg);
        this.scrollToBottom();

        const payload = {
          model: this.selectedModel?.id || 'gemma3:4b',
          messages: [{ role: 'user', content: maskedContent }],
          stream: true,
          overridePrivacyFilter: true
        };

        this.currentAbortController = new AbortController();
        const serverResponse = await secureChatAPI.sendMessage(
          this.currentChat?.sessionId || `temp_${Date.now()}`,
          payload,
          {
            returnStream: true,
            overridePrivacyFilter: true,  // PII 필터 우회
            signal: this.currentAbortController.signal
          }
        );

        // 로딩 메시지 제거
        const loadingIdx = this.messages.findIndex(m => m.id === loadingMsg.id);
        if (loadingIdx !== -1) {
          this.messages.splice(loadingIdx, 1);
        }

        // 스트림 처리
        if (serverResponse instanceof ReadableStream || (serverResponse && typeof serverResponse.getReader === 'function')) {
          const assistantMsg = {
            id: 'assistant_' + Date.now(),
            role: 'assistant',
            content: '',
            modelName: this.selectedModel?.name || this.selectedModel?.id || '',
            timestamp: null,
            reference_docs: []
          };
          this.messages.push(assistantMsg);
          const assistantIdx = this.messages.length - 1;
          // 마스킹된 콘텐츠로 새 userMessage 생성
          const maskedUserMessage = { ...userMessage, content: maskedContent };
          await this.processRealStream(serverResponse, assistantIdx, maskedUserMessage);
        } else {
          // 일반 응답 처리
          const normalized = normalizeChatResponse(serverResponse);
          const assistantMsg = {
            id: normalized.id || 'assistant_' + Date.now(),
            role: 'assistant',
            content: normalized.content,
            modelName: this.selectedModel?.name || this.selectedModel?.id || '',
            timestamp: Date.now(),
            reference_docs: normalized.reference_docs || []
          };
          this.messages.push(assistantMsg);
        }
      } catch (err) {
        console.error('[handleMaskingConfirm] 재전송 실패:', err);
        this.alertMessage = '메시지 재전송에 실패했습니다.';
        this.showAlertModal = true;
      } finally {
        this.isApiCalling = false;
        this.isWaitingResponse = false;
        this.maskingLoading = false;
        // 안전장치: 남아있는 로딩 메시지 제거
        const remainingLoading = this.messages.findIndex(m => m.role === 'loading');
        if (remainingLoading !== -1) {
          this.messages.splice(remainingLoading, 1);
        }
        this.scrollToBottom();
      }
    },
    // 인라인 마스킹 모달 - 취소
    handleMaskingCancel() {
      this.showMaskingModal = false;
      this.maskingState = null;
      this.maskingParts = [];
      this.maskingUserMessage = null;
      this.maskingLoading = false;
    },
    // 마스킹 파트 배열 생성 (인라인 표시용)
    initializeMaskingParts() {
      if (!this.maskingState) return;
      const { maskedUserContent, detectedPii } = this.maskingState;
      const parts = [];
      let lastIdx = 0;

      detectedPii.forEach((item, i) => {
        const start = item.start;
        const end = item.end;

        // 일반 텍스트 부분 추가
        if (start > lastIdx) {
          parts.push({
            text: maskedUserContent.slice(lastIdx, start),
            isPii: false,
            index: -1
          });
        }

        // PII 부분 추가
        parts.push({
          text: maskedUserContent.slice(start, end),
          isPii: true,
          index: i,
          originalText: item.originalText,
          maskedText: item.maskedText,
          entityType: item.entityType
        });

        lastIdx = end;
      });

      // 마지막 일반 텍스트 부분 추가
      if (lastIdx < maskedUserContent.length) {
        parts.push({
          text: maskedUserContent.slice(lastIdx),
          isPii: false,
          index: -1
        });
      }

      this.maskingParts = parts;
    },
    // 인라인 마스킹 표시용 파트 배열 반환
    getMaskedParts() {
      if (!this.maskingParts || this.maskingParts.length === 0) return [];

      return this.maskingParts.map(part => {
        if (part.isPii) {
          return {
            type: 'pii',
            idx: part.index,
            text: part.originalText,
            entityType: part.entityType,
            maskedText: part.maskedText
          };
        } else {
          return {
            type: 'text',
            text: part.text
          };
        }
      });
    },
    // 마스킹된 텍스트 반환
    getMaskedText(item, masked) {
      if (masked) {
        return item.maskedText || item.text || '';
      }
      return item.text || '';
    },
    // 체크박스 변경 시 마스킹 상태 업데이트
    updateMasked() {
      if (!this.maskingParts || !this.maskingState) return;

      // 각 PII 파트의 텍스트를 체크박스 상태에 따라 업데이트
      this.maskingParts.forEach(part => {
        if (part.isPii) {
          const isChecked = this.maskingState.masked[part.index];
          if (isChecked) {
            part.text = part.maskedText;
          } else {
            part.text = part.originalText;
          }
        }
      });

      // 최종 결과 업데이트
      this.maskingState.maskedUserContent = this.maskingParts.map(part => part.text).join('');
    },
    // 최종 마스킹된 사용자 콘텐츠 반환
    getMaskedUserContent() {
      if (!this.maskingParts || this.maskingParts.length === 0) return '';
      return this.maskingParts.map(part => part.text).join('');
    },
    async handleEditMessage({ messageId, content }) {
      try {
        const messageIndex = this.messages.findIndex(msg => msg.id === messageId)
        if (messageIndex === -1) return
        
        // 메시지 업데이트
        const updatedMessage = {
          ...this.messages[messageIndex],
          content,
          timestamp: Date.now() // 수정 시간 업데이트
        }
        
        this.messages.splice(messageIndex, 1, updatedMessage)
        
        // API 호출로 서버에 메시지 업데이트
        await chatAPI.updateMessage(this.currentChat.id, messageId, content)
      } catch (error) {
        console.error('메시지 수정 중 오류 발생:', error)
      }
    },
    // 사용자 메시지 내용 업데이트 핸들러 (마스킹 교체용)
    handleUpdateUserMessage({ index, content }) {
      if (index >= 0 && index < this.messages.length) {
        this.$set(this.messages, index, {
          ...this.messages[index],
          content
        });
      }
    },
    // 로딩 메시지 제거 핸들러
    handleRemoveLoadingMessage() {
      const loadingIdx = this.messages.findIndex(m => m.role === 'loading');
      if (loadingIdx !== -1) {
        this.messages.splice(loadingIdx, 1);
      }
    },
    // 로딩 메시지 추가 핸들러
    handleAddLoadingMessage(loadingMessage) {
      // 이미 로딩 메시지가 있으면 추가하지 않음
      if (this.messages.some(m => m.role === 'loading')) {
        return;
      }
      this.messages.push(loadingMessage);
      this.scrollToBottom();
    },
    /**
     * 채팅 메시지 전송 처리를 위한 메인 핸들러
     * - 새 채팅과 기존 채팅 구분하여 처리
     * - ChatInput 컴포넌트로부터 메시지를 받아 적절한 처리 로직으로 전달
     */
    handleSendMessage(userMessage) {
      console.log('[Chat.vue] ChatInput에서 메시지 수신:', userMessage);
      
      if (this.messages.length === 0) {
        // 새 채팅: Chat.vue에서 직접 처리 (기존 로직 사용)
        console.log('[Chat.vue] 새 채팅 - Chat.vue에서 직접 처리');
        this.handleDirectMessage(userMessage);
      } else {
        // 기존 채팅: ChatMessage 컴포넌트에 전달
        console.log('[Chat.vue] 기존 채팅 - ChatMessage로 전달');
        const chatMessageComponent = this.$children.find(child => 
          child.$options.name === 'ChatMessage'
        );
        
        if (chatMessageComponent) {
          chatMessageComponent.handleSendMessage(userMessage);
        } else {
          console.error('[Chat.vue] ChatMessage 컴포넌트를 찾을 수 없음');
          // 대안으로 기존 로직 사용
          this.handleDirectMessage(userMessage);
        }
      }
      
      // ChatInput 초기화
      if (this.$refs.chatInputEmpty) {
        this.$refs.chatInputEmpty.resetState();
      }
      if (this.$refs.chatInputMain) {
        this.$refs.chatInputMain.resetState();
      }
    },
    /**
     * 새 채팅에서 직접 메시지를 처리하는 메서드
     * - SDK 체크 및 초기화 확인
     * - 메시지 암호화 및 전송
     * - 응답 처리 및 화면 업데이트
     */
    async handleDirectMessage(userMessage) {
        console.log('[handleDirectMessage] 진입');
        
        if (!this.checkSDK()) return;  // SDK 체크
        
        if (!userMessage || !userMessage.content) {
          console.log('[handleDirectMessage] 유효하지 않은 userMessage', userMessage);
          return;
        }
        
        if (!userMessage.content.trim() && (!userMessage.files || userMessage.files.length === 0)) {
          return;
        }
        if (!this.selectedModel) {
          this.modelPlaceholder = '모델을 선택해 주세요';
          return;
        }
        
      // isWaitingResponse 설정하지 않음 - ChatInput 로딩 방지
        if (this.messages.length === 0) {
          this.isModelSelectionDisabled = true;
        }
        
        let loadingMsg = null;
        let userMsg = null;
        
        try {
          // SDK 초기화 확인
          if (!this.$sdk) {
            console.error('[Chat.vue] SDK가 아직 초기화되지 않았습니다.');
            this.alertMessage = 'SDK 초기화 중입니다. 잠시 후 다시 시도해주세요.';
            this.showAlertModal = true;
            return;
          }
          
          // 암호화 처리
          const { encrypted } = await processRequest({ content: userMessage.content, sdk: this.$sdk });
          
          const now = Date.now();
          userMsg = {
            id: 'user_' + now,
            role: 'user',
            content: userMessage.content,
            encryptedContent: encrypted,
            originalContent: userMessage.content,
            timestamp: now - 2000,
            modelName: this.selectedModel?.name || this.selectedModel?.id || ''
          };
          
          // 파일 첨부 처리
          if (userMessage.files && userMessage.files.length > 0) {
            const pureFiles = (userMessage.files || []).map(f => Object.assign({}, f));
            const uniqueFiles = [];
            const seen = new Set();
            for (const f of pureFiles) {
              const key = `${f.id}_${f.size}_${f.name}`;
              if (!seen.has(key)) {
                uniqueFiles.push(f);
                seen.add(key);
              }
            }
            userMsg.metadata = {
              attachments: uniqueFiles.map(f => ({
                id: f.id,
                size: f.size,
                name: f.name,
                mime_type: f.mime_type
              }))
            };
          }
          
          this.messages = [...this.messages, userMsg];
          this.messages = this.ensureModerationWarnings(this.messages);
          
          // 로딩 메시지 추가
          loadingMsg = {
            id: 'loading_' + Date.now(),
            role: 'loading',
            content: userMessage.content,
            modelName: this.selectedModel?.name || this.selectedModel?.id || '',
            timestamp: null
          };
          this.messages = [...this.messages, loadingMsg];
          this.messages = this.ensureModerationWarnings(this.messages);
          this.scrollToBottom();
          
          // 임시 채팅 기록 관리
          if (this.currentChat && this.currentChat.isTemp) {
            const idx = this.chatHistory.findIndex(c => c.sessionId === this.currentChat.sessionId && c.isTemp);
            if (idx === -1) {
              const tempChat = {
                ...this.currentChat,
                title: userMessage.content,
                messages: [userMsg],
                created_at_ms: Date.now(),
                created_at: new Date().toISOString()
              };
              this.chatHistory = [tempChat, ...this.chatHistory];
            } else {
              this.chatHistory[idx].title = userMessage.content;
              this.chatHistory[idx].messages = [userMsg];
          }
        }
        
        // API 호출 - secureChatAPI 사용
        const api = secureChatAPI;
        const payload = {
          model: this.selectedModel?.id || 'gemma3:4b',
          messages: [
            {
              role: 'user',
              content: userMessage.content.trim(),
              metadata: userMessage.files && userMessage.files.length > 0 ? {
                attachments: userMessage.files.map(f => ({
                  id: f.id,
                  name: f.name,
                  size: f.size,
                  mime_type: f.mime_type || 'application/pdf'
                }))
              } : undefined
            }
          ],
          stream: true
        };

        // SDK v1.2.0: sessionId 사용 (스트림 제어를 위해)
        const chatId = this.currentChat?.sessionId || this.currentChat?.id || 'new';
        let serverResponse;

        try {
          // ✅ SDK 호출 직전 시간 기록
          const requestTime = Date.now();

          // API 호출 시작
          this.isApiCalling = true;
          console.log('[handleDirectMessage] API 호출 시작, isApiCalling:', this.isApiCalling);

          // SDK v1.2.1: 스트림 직접 반환 요청 (AbortController)
          this.currentAbortController = new AbortController();
          serverResponse = await api.sendMessage(chatId, payload, { returnStream: true, signal: this.currentAbortController.signal });

          // ✅ API 호출 완료 후 사용자 메시지 시간을 요청 시점으로 업데이트
          if (userMsg) {
            const userMsgIndex = this.messages.findIndex(m => m.id === userMsg.id);
            if (userMsgIndex !== -1) {
              this.$set(this.messages[userMsgIndex], 'timestamp', requestTime);
            }
          }

          // SDK v1.2.0: ReadableStream인 경우 실제 스트리밍 처리
          if (serverResponse instanceof ReadableStream || (serverResponse && typeof serverResponse.getReader === 'function')) {
            console.log('[handleDirectMessage] 실제 스트리밍 응답 감지');

            // 로딩 메시지 제거
            const loadingIdx = this.messages.findIndex(m => m.id === loadingMsg.id);
            if (loadingIdx !== -1) {
              this.messages.splice(loadingIdx, 1);
            }

            // 어시스턴트 메시지 추가 (빈 상태로 시작)
            const assistantMsg = {
              id: 'assistant_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
              role: 'assistant',
              content: '',
              timestamp: null,
              modelName: this.selectedModel?.name || this.selectedModel?.id || '',
              encryptedContent: '',
              metadata: null,
              reference_docs: [],
              sessionId: this.currentChat?.sessionId || '',
              isError: false
            };
            this.messages = [...this.messages, assistantMsg];
            const assistantIdx = this.messages.length - 1;
            this.scrollToBottom();

            // 실제 스트림 처리 (userMsg 전달 - PII 에러 시 재전송용)
            await this.processRealStream(serverResponse, assistantIdx, userMsg);

            // 임시 기록을 실제 기록으로 교체 (스트림 완료 후)
            if (this.currentChat && this.currentChat.isTemp && !this.isPaused) {
              const idx = this.chatHistory.findIndex(c => c.sessionId === this.currentChat.sessionId && c.isTemp);
              if (idx !== -1) {
                const updatedChat = {
                  ...this.chatHistory[idx],
                  isTemp: false,
                  messages: [userMsg, this.messages[assistantIdx]]
                };
                this.chatHistory.splice(idx, 1, updatedChat);
                this.currentChat = updatedChat;
                console.log('[handleDirectMessage] 임시 기록을 실제 기록으로 교체 완료');
              }
            }

            return; // 스트리밍 처리 완료
          }
        } catch (err) {
          serverResponse = err;
        } finally {
          // API 호출 완료 (성공/실패 관계없이)
          this.isApiCalling = false;
          console.log('[handleDirectMessage] API 호출 완료, isApiCalling:', this.isApiCalling);
        }
        
        if (serverResponse && serverResponse.status && serverResponse.status !== 200) {
          // 422 에러는 ChatMessage 컴포넌트로 위임하여 처리합니다.
          if (serverResponse.status === 422) {
            const chatMessageComponent = this.$refs.chatMessageComponent || this.$refs.chatMessageComponentMain;
            if (chatMessageComponent) {
              await chatMessageComponent.processApiRequest(userMessage, serverResponse);
            }
            return;
          }
          // 그 외 200이 아닌 모든 에러는 여기서 직접 처리합니다.
          else {
            handleApiError(serverResponse, {
              showModal: (msg) => {
                this.alertMessage = msg;
                this.showAlertModal = true;
                const loadingIdx = this.messages.findIndex(m => m.id === loadingMsg.id);
                if (loadingIdx !== -1) {
                    this.messages.splice(loadingIdx, 1);
                }
              },
              handleMasking: async (res) => {
                // PII 에러(2100-2199) 처리
                console.log('[handleDirectMessage] PII 에러 감지:', res);

                // 로딩 메시지 제거
                const loadingIdx = this.messages.findIndex(m => m.id === loadingMsg.id);
                if (loadingIdx !== -1) {
                  this.messages.splice(loadingIdx, 1);
                }

                // predictions 추출: _meta.detail.predictions.detectedPii (SDK) 또는 error.detail.predictions (레거시)
                let piiDetail = res?._meta?.detail || res?.error?.detail;
                if (typeof piiDetail === 'string') {
                  try { piiDetail = JSON.parse(piiDetail); } catch (e) { /* 그대로 사용 */ }
                }
                const predictions = piiDetail?.predictions?.detectedPii || (Array.isArray(piiDetail?.predictions) ? piiDetail.predictions : []);
                console.log('[handleDirectMessage] predictions:', predictions);

                if (predictions.length > 0) {
                  // 마스킹된 텍스트 생성 + maskedContent 기준 오프셋 계산
                  const sortedByStart = [...predictions].sort((a, b) => a.originalStart - b.originalStart);

                  let maskedContent = '';
                  let lastOrigIdx = 0;
                  const adjustedPredictions = sortedByStart.map((pred) => {
                    const plainText = userMessage.content.slice(lastOrigIdx, pred.originalStart);
                    maskedContent += plainText;

                    const newStart = maskedContent.length;
                    maskedContent += pred.maskedText;
                    const newEnd = maskedContent.length;

                    lastOrigIdx = pred.originalEnd;

                    return { ...pred, start: newStart, end: newEnd };
                  });
                  maskedContent += userMessage.content.slice(lastOrigIdx);

                  // maskingState 설정 (인라인 UI용)
                  this.maskingState = {
                    maskedUserContent: maskedContent,
                    userContent: userMessage.content,
                    detectedPii: adjustedPredictions,
                    masked: adjustedPredictions.map(() => true)
                  };

                  // 마스킹 파트 배열 초기화
                  this.initializeMaskingParts();

                  // ✅ userMsg (채팅에 추가된 메시지 객체, id 포함)를 저장
                  this.maskingUserMessage = userMsg;
                  this.showMaskingModal = true;
                } else {
                  // predictions가 없으면 간단한 확인 다이얼로그 사용
                  this.piiConfirmData = {
                    userMessage: userMessage,
                    errorMessage: res?.error?.message || res?.message || '개인정보가 감지되었습니다.'
                  };
                  this.showPiiConfirmModal = true;
                }
              }
            });
            return;
          }
        }
        
        // 응답 처리
        const normalized = normalizeChatResponse(serverResponse);
        
        // ✅ 응답 중지 상태 체크: 페이지 이동이나 중지 요청이 있었으면 무시
        if (this.isPaused) {
          console.log('[handleDirectMessage] 중지 상태로 응답 무시');
          // 로딩 메시지만 제거하고 종료
          const loadingIdx = this.messages.findIndex(m => m.id === loadingMsg.id);
          if (loadingIdx !== -1) {
            this.messages.splice(loadingIdx, 1);
          }
          return;
        }
        
        // ✅ 컴포넌트 상태 체크: 페이지가 /chat이 아니거나 unmounted 상태면 무시
        if (this.$route.path !== '/chat' || !this.currentChat) {
          console.log('[handleDirectMessage] 잘못된 페이지 상태로 응답 무시:', {
            path: this.$route.path,
            currentChat: !!this.currentChat
          });
          // 로딩 메시지만 제거하고 종료
          const loadingIdx = this.messages.findIndex(m => m.id === loadingMsg.id);
          if (loadingIdx !== -1) {
            this.messages.splice(loadingIdx, 1);
          }
          return;
        }
        
        // ✅ sessionId 검증: 현재 채팅과 다른 세션의 응답이면 무시
        if (serverResponse.sessionId && this.currentChat?.sessionId && 
            serverResponse.sessionId !== this.currentChat.sessionId) {
          console.log('[handleDirectMessage] 다른 세션의 응답 무시:', {
            serverSessionId: serverResponse.sessionId,
            currentSessionId: this.currentChat.sessionId
          });
          // 로딩 메시지만 제거하고 종료
          const loadingIdx = this.messages.findIndex(m => m.id === loadingMsg.id);
          if (loadingIdx !== -1) {
            this.messages.splice(loadingIdx, 1);
          }
          return;
        }
        
          if (normalized && typeof normalized.content === 'string') {
          // 로딩 메시지 제거
            const loadingIdx = this.messages.findIndex(m => m.id === loadingMsg.id);
            if (loadingIdx !== -1) {
              this.messages.splice(loadingIdx, 1);
            }
          
            const assistantMsg = {
              id: 'assistant_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
              role: normalized.role,
              content: '',
              timestamp: null,
              modelName: this.selectedModel?.name || this.selectedModel?.id || '',
              encryptedContent: normalized.encryptedContent || '',
              metadata: normalized.metadata || null,
              reference_docs: [],
              sessionId: this.currentChat?.sessionId || '',
            isError: normalized.isError || serverResponse.isError || false // 에러 메시지 여부
            };
          
            this.messages = [...this.messages, assistantMsg];
            const assistantIdx = this.messages.length - 1;
            this.scrollToBottom();
            
          // 에러 메시지는 타이핑 효과 없이 즉시 표시
          if (normalized.isError || serverResponse.isError) {
            this.$set(this.messages, assistantIdx, {
              ...this.messages[assistantIdx],
              content: normalized.content,
              timestamp: Date.now(),
              reference_docs: normalized.reference_docs || []
            });
            this.scrollToBottom();
          } else {
            // 정상 응답은 타이핑 효과
            this.isTyping = true; // 타이핑 시작
            
            // ✅ 타이핑 효과 시작 전 중지 상태 체크 (채팅 이동 시 즉시 중단)
            if (this.isPaused) return;
            
            // ✅ created_at 시간을 미리 저장 (타이핑 완료 후 사용)
            let responseTimestamp = Date.now(); // 기본값
            if (normalized.created_at) {
              responseTimestamp = new Date(normalized.created_at).getTime();
            }
            
            let displayed = '';
            for (let i = 0; i < normalized.content.length; i++) {
              if (this.isPaused) break;
              displayed += normalized.content[i];
              this.$set(this.messages, assistantIdx, {
                ...this.messages[assistantIdx],
                content: displayed
              });
              
              if (i % 10 === 0 || i === normalized.content.length - 1) {
                this.scrollToBottom();
              }
              
              await new Promise(res => setTimeout(res, 15));
            }
            
            // 중지 요청이 없었던 경우에만 isPaused를 false로 설정
            if (!this.isPaused) {
              console.log('[handleDirectMessage] 정상 완료 - isPaused 유지 false');
            } else {
              console.log('[handleDirectMessage] 중지 상태였음 - isPaused 유지 true');
            }
            this.isTyping = false; // 타이핑 완료
            
            // ✅ 타이핑 완료 후 시간과 reference_docs 함께 설정
            this.$set(this.messages, assistantIdx, {
              ...this.messages[assistantIdx],
              timestamp: responseTimestamp,
              reference_docs: normalized.reference_docs || []
            });

            // 새채팅 토큰 사용량 갱신
            try {
              const usageRes = await chatAPI.getTokenUsage();
              if (usageRes && usageRes.data) {
                this.tokenUsage = usageRes.data;
              }
            } catch (e) {
              console.error('[Token Usage] 새채팅 토큰 사용량 갱신 오류', e);
            }
            
            this.scrollToBottom();
          }
            
            // 임시 기록을 실제 기록으로 교체
            if (serverResponse.sessionId && this.currentChat && this.currentChat.isTemp) {
              console.log('[Chat.vue] 임시 기록을 실제 기록으로 교체', serverResponse.sessionId);
            const userMsg = this.messages.find(m => m.role === 'user');
              const idx = this.chatHistory.findIndex(c => c.sessionId === this.currentChat.sessionId && c.isTemp);
            if (idx !== -1 && userMsg) {
                const updatedChat = {
                  ...this.chatHistory[idx],
                  id: serverResponse.sessionId,
                  sessionId: serverResponse.sessionId,
                  isTemp: false,
                  messages: [userMsg, assistantMsg]
                };
                this.chatHistory.splice(idx, 1, updatedChat);
                this.currentChat = updatedChat;
                console.log('[Chat.vue] 실제 기록으로 교체 완료', updatedChat);
            }
          }
        }
        
      } catch (error) {
        console.error('[Chat.vue] handleDirectMessage 에러:', error);
        this.alertMessage = error.message || '네트워크 오류가 발생했습니다.';
            this.showAlertModal = true;
      }
    },
    /**
     * 메시지 전송 성공 시의 처리를 담당하는 메서드
     * - 사용자 메시지와 서버 응답 처리
     * - 화면 업데이트 및 상태 관리
     * - 임시 채팅 처리
     */
    async handleMessageSuccess({ userMessage, serverResponse, loadingMessage, cancelled }) {
      console.log('[Chat.vue] handleMessageSuccess 진입', userMessage, serverResponse, loadingMessage, cancelled);
      try {
        // 취소된 경우
        if (cancelled) {
          console.log('[Chat.vue] 모달 취소됨 - 대기 상태 해제');
            this.isWaitingResponse = false;
          // 로딩 메시지 제거
          const loadingIdx = this.messages.findIndex(m => m.role === 'loading');
          if (loadingIdx !== -1) {
            this.messages.splice(loadingIdx, 1);
          }
            return;
          }
        
        // 새로운 구조: userMessage와 loadingMessage가 함께 온 경우
        if (userMessage && loadingMessage) {
          console.log('[Chat.vue] 사용자 메시지와 로딩 메시지 순차 추가');
          
          // 암호화 처리
          const { encrypted } = await processRequest({ content: userMessage.content, sdk: this.$sdk });
          
          const now = Date.now();
          const processedUserMsg = {
            id: 'user_' + now,
            role: 'user',
            content: userMessage.content,
            encryptedContent: encrypted,
            originalContent: userMessage.content,
            timestamp: now - 2000,
            modelName: this.selectedModel?.name || this.selectedModel?.id || '',
            metadata: userMessage.metadata
          };
          
          // ✅ 순차적 메시지 추가로 순서 보장
          // 1. 사용자 메시지 먼저 추가
          this.messages = [...this.messages, processedUserMsg];
          this.messages = this.ensureModerationWarnings(this.messages);
          
          // DOM 업데이트 대기 후 로딩 메시지 추가
          await this.$nextTick();
          
          // 2. 로딩 메시지 추가 (timestamp 조정으로 순서 보장)
          const adjustedLoadingMessage = {
            ...loadingMessage,
            timestamp: now - 1000 // 사용자 메시지보다 늦은 시간
          };
          
          this.messages = [...this.messages, adjustedLoadingMessage];
          this.messages = this.ensureModerationWarnings(this.messages);
          
          // isWaitingResponse 설정하지 않음 - ChatInput 로딩 방지
          
          // 임시 채팅 기록 관리
          if (this.currentChat && this.currentChat.isTemp) {
            const idx = this.chatHistory.findIndex(c => c.sessionId === this.currentChat.sessionId && c.isTemp);
            if (idx === -1) {
                          const tempChat = {
              ...this.currentChat,
              title: userMessage.content,
              messages: [processedUserMsg],
              created_at_ms: Date.now(),
              created_at: new Date().toISOString()
            };
              this.chatHistory = [tempChat, ...this.chatHistory];
            } else {
                          this.chatHistory[idx].title = userMessage.content;
            this.chatHistory[idx].messages = [processedUserMsg];
            }
          }
          
          this.scrollToBottom();
          return;
        }
        
        // serverResponse가 있는 경우 응답 처리
        if (serverResponse) {
          console.log('[Chat.vue] 서버 응답 처리');

          // ✅ 응답 중지 상태 체크: 페이지 이동이나 중지 요청이 있었으면 무시
          if (this.isPaused) {
            console.log('[handleMessageSuccess] 중지 상태로 응답 무시');
            return;
          }

          console.log('[handleMessageSuccess] 모든 검증 통과, isPaused:', this.isPaused, 'currentChat:', this.currentChat?.sessionId);

          // ✅ 컴포넌트 상태 체크: 페이지가 /chat이 아니거나 unmounted 상태면 무시
          if (this.$route.path !== '/chat' || !this.currentChat) {
            console.log('[handleMessageSuccess] 잘못된 페이지 상태로 응답 무시:', {
              path: this.$route.path,
              currentChat: !!this.currentChat
            });
            return;
          }

          // ✅ sessionId 검증: 현재 채팅과 다른 세션의 응답이면 무시
          if (serverResponse.sessionId && this.currentChat?.sessionId &&
              serverResponse.sessionId !== this.currentChat.sessionId) {
            console.log('[handleMessageSuccess] 다른 세션의 응답 무시:', {
              serverSessionId: serverResponse.sessionId,
              currentSessionId: this.currentChat.sessionId
            });
            return;
          }

          await this.processServerResponse(serverResponse);
          return;
        }
        
        // userMessage만 있는 경우 (재전송 시)
        if (userMessage && !serverResponse && !loadingMessage) {
          console.log('[Chat.vue] 재전송 사용자 메시지 추가');
          
          // 암호화 처리
          const { encrypted } = await processRequest({ content: userMessage.content, sdk: this.$sdk });
          
          const processedUserMsg = {
            ...userMessage,
            encryptedContent: encrypted,
            originalContent: userMessage.content
          };
          
          // 사용자 메시지 추가
          this.messages = [...this.messages, processedUserMsg];
          this.messages = this.ensureModerationWarnings(this.messages);
          this.scrollToBottom();
          return;
        }
        
        // 기존 로직 (호환성 유지)
        if (!userMessage || !userMessage.content) {
          console.log('[Chat.vue] 유효하지 않은 userMessage', userMessage);
          return;
        }
        if (!this.selectedModel) {
          this.modelPlaceholder = '모델을 선택해 주세요';
          console.log('[Chat.vue] selectedModel 없음');
          return;
        }
        
        // isWaitingResponse 설정하지 않음 - ChatInput 로딩 방지
        if (this.messages.length === 0) {
          this.isModelSelectionDisabled = true;
        }
        
        // 기존 처리 로직...
        console.log('[Chat.vue] 기존 로직으로 처리');
        
        } catch (error) {
        console.error('[Chat.vue] handleMessageSuccess 에러:', error);
        this.alertMessage = error.message || '네트워크 오류가 발생했습니다.';
          this.showAlertModal = true;
      }
    },
    // 사용자 메시지만 처리하는 메서드 (ChatMessage에서 첫 번째 emit용)
    /**
     * 사용자 메시지만 독립적으로 처리하는 메서드
     * - SDK 체크 및 초기화 확인
     * - 메시지 암호화
     * - 화면 업데이트
     */
    async processUserMessage(userMessage) {
      console.log('[Chat.vue] processUserMessage 진입', userMessage);
      
      if (!this.checkSDK()) return;  // SDK 체크
      
      if (!this.selectedModel) {
        this.modelPlaceholder = '모델을 선택해 주세요';
        console.log('[Chat.vue] selectedModel 없음');
        return;
      }
      
      this.isWaitingResponse = true;
      if (this.messages.length === 0) {
        this.isModelSelectionDisabled = true;
      }
      
      try {
        // SDK 초기화 확인
        if (!this.$sdk) {
          console.error('[Chat.vue] SDK가 아직 초기화되지 않았습니다.');
          this.alertMessage = 'SDK 초기화 중입니다. 잠시 후 다시 시도해주세요.';
          this.showAlertModal = true;
          return;
        }
        
        // 암호화 처리
        const { encrypted } = await processRequest({ content: userMessage.content, sdk: this.$sdk });
      
      const now = Date.now();
      const userMsg = {
        id: 'user_' + now,
        role: 'user',
        content: userMessage.content,
        encryptedContent: encrypted,
        originalContent: userMessage.content,
        timestamp: now - 2000,
        modelName: this.selectedModel?.name || this.selectedModel?.id || ''
      };
      
      // 파일 첨부 처리
      if (userMessage.files && userMessage.files.length > 0) {
        const pureFiles = (userMessage.files || []).map(f => Object.assign({}, f));
        const uniqueFiles = [];
        const seen = new Set();
        for (const f of pureFiles) {
          const key = `${f.id}_${f.size}_${f.name}`;
          if (!seen.has(key)) {
            uniqueFiles.push(f);
            seen.add(key);
          }
        }
        userMsg.metadata = {
          attachments: uniqueFiles.map(f => ({
            id: f.id,
            size: f.size,
            name: f.name,
            mime_type: f.mime_type
          }))
        };
      }
      
      this.messages = [...this.messages, userMsg];
      this.messages = this.ensureModerationWarnings(this.messages);
      
      // 로딩 메시지 추가
      const loadingMsg = {
        id: 'loading_' + Date.now(),
        role: 'loading',
        content: userMessage.content,
        modelName: this.selectedModel?.name || this.selectedModel?.id || '',
        timestamp: null
      };
      this.messages = [...this.messages, loadingMsg];
      this.messages = this.ensureModerationWarnings(this.messages);
      this.scrollToBottom();
      
      // 임시 채팅 기록 관리
      if (this.currentChat && this.currentChat.isTemp) {
        const idx = this.chatHistory.findIndex(c => c.sessionId === this.currentChat.sessionId && c.isTemp);
        if (idx === -1) {
          const tempChat = {
            ...this.currentChat,
            title: userMessage.content,
            messages: [userMsg],
            created_at_ms: Date.now(),
            created_at: new Date().toISOString()
          };
          this.chatHistory = [tempChat, ...this.chatHistory];
          console.log('[Chat.vue] 임시 기록 추가', tempChat);
        } else {
          this.chatHistory[idx].title = userMessage.content;
          this.chatHistory[idx].messages = [userMsg];
          console.log('[Chat.vue] 임시 기록 갱신', this.chatHistory[idx]);
        }
      }
      
    } catch (err) {
      console.error('[Chat.vue] processUserMessage 에러', err);
      this.alertMessage = '메시지 처리 중 오류가 발생했습니다.';
      this.showAlertModal = true;
        this.isWaitingResponse = false;
    }
    },
    // 서버 응답만 처리하는 메서드 (ChatMessage에서 두 번째 emit용)
    /**
     * 서버 응답만 독립적으로 처리하는 메서드
     * - 응답 정규화
     * - 타이핑 효과 처리
     * - 화면 업데이트
     */
    async processServerResponse(serverResponse) {
      console.log('[Chat.vue] processServerResponse 진입', serverResponse);
      
      try {
        // 로딩 메시지 제거
        const loadingIdx = this.messages.findIndex(m => m.role === 'loading');
        if (loadingIdx !== -1) {
          this.messages.splice(loadingIdx, 1);
        }
        
        // 서버 응답 정규화
        const normalized = normalizeChatResponse(serverResponse);
        
        // ✅ 응답 중지 상태 체크: 페이지 이동이나 중지 요청이 있었으면 무시
        if (this.isPaused) {
          console.log('[processServerResponse] 중지 상태로 응답 무시');
          return;
        }
        
        // ✅ 컴포넌트 상태 체크: 페이지가 /chat이 아니거나 unmounted 상태면 무시
        if (this.$route.path !== '/chat' || !this.currentChat) {
          console.log('[processServerResponse] 잘못된 페이지 상태로 응답 무시:', {
            path: this.$route.path,
            currentChat: !!this.currentChat
          });
          return;
        }
        
        // ✅ sessionId 검증: 현재 채팅과 다른 세션의 응답이면 무시
        if (serverResponse.sessionId && this.currentChat?.sessionId && 
            serverResponse.sessionId !== this.currentChat.sessionId) {
          console.log('[processServerResponse] 다른 세션의 응답 무시:', {
            serverSessionId: serverResponse.sessionId,
            currentSessionId: this.currentChat.sessionId
          });
          return;
        }
        
        if (normalized && typeof normalized.content === 'string') {
          const assistantMsg = {
            id: 'assistant_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
            role: normalized.role,
            content: '',
            timestamp: null,
            modelName: this.selectedModel?.name || this.selectedModel?.id || '',
            encryptedContent: normalized.encryptedContent || '',
            metadata: normalized.metadata || null,
            reference_docs: [],
            sessionId: this.currentChat?.sessionId || '',
            isError: normalized.isError || serverResponse.isError || false // 에러 메시지 여부
          };
          
          this.messages = [...this.messages, assistantMsg];
          const assistantIdx = this.messages.length - 1;
          this.scrollToBottom();
          
          // 에러 메시지는 타이핑 효과 없이 즉시 표시
          if (normalized.isError || serverResponse.isError) {
            this.$set(this.messages, assistantIdx, {
              ...this.messages[assistantIdx],
              content: normalized.content,
              timestamp: Date.now(),
              reference_docs: normalized.reference_docs || []
            });
            this.scrollToBottom();
          } else {
            // 정상 응답은 타이핑 효과
            this.isTyping = true; // 타이핑 시작
            
            // ✅ 타이핑 효과 시작 전 중지 상태 체크 (채팅 이동 시 즉시 중단)
            if (this.isPaused) return;
            
            // ✅ created_at 시간을 미리 저장 (타이핑 완료 후 사용)
            let responseTimestamp = Date.now(); // 기본값
            if (normalized.created_at) {
              responseTimestamp = new Date(normalized.created_at).getTime();
            }
            
            let displayed = '';
            for (let i = 0; i < normalized.content.length; i++) {
              if (this.isPaused) break;
              displayed += normalized.content[i];
              this.$set(this.messages, assistantIdx, {
                ...this.messages[assistantIdx],
                content: displayed
              });
              
              if (i % 10 === 0 || i === normalized.content.length - 1) {
                this.scrollToBottom();
              }
              
              await new Promise(res => setTimeout(res, 15));
            }
            
            // 중지 요청이 없었던 경우에만 isPaused를 false로 설정
            if (!this.isPaused) {
              console.log('[processServerResponse] 정상 완료 - isPaused 유지 false');
            } else {
              console.log('[processServerResponse] 중지 상태였음 - isPaused 유지 true');
            }
            this.isTyping = false; // 타이핑 완료
            
            // ✅ 타이핑 완료 후 시간과 reference_docs 함께 설정
            this.$set(this.messages, assistantIdx, {
              ...this.messages[assistantIdx],
              timestamp: responseTimestamp,
              reference_docs: normalized.reference_docs || []
            });
            
            this.scrollToBottom();
          }
            
            // 임시 기록을 실제 기록으로 교체
            if (serverResponse.sessionId && this.currentChat && this.currentChat.isTemp) {
              console.log('[Chat.vue] 임시 기록을 실제 기록으로 교체', serverResponse.sessionId);
            const userMsg = this.messages.find(m => m.role === 'user');
            const idx = this.chatHistory.findIndex(c => c.sessionId === this.currentChat.sessionId && c.isTemp);
            if (idx !== -1 && userMsg) {
                const updatedChat = {
                  ...this.chatHistory[idx],
                  id: serverResponse.sessionId,
                  sessionId: serverResponse.sessionId,
                  isTemp: false,
                  messages: [userMsg, assistantMsg]
                };
                this.chatHistory.splice(idx, 1, updatedChat);
                this.currentChat = updatedChat;
                console.log('[Chat.vue] 실제 기록으로 교체 완료', updatedChat);
            }
          }
          
          // 디버그 모드일 때 암호화 텍스트 동기화
          if (this.debugMode) {
            await this.encryptVisibleMessages();
          }
          
          this.messages = this.ensureModerationWarnings(this.messages);
          
        } else {
          console.error('content가 비어있거나 잘못됨', normalized, serverResponse);
          this.alertMessage = '서버 응답에 내용이 없습니다.';
          this.showAlertModal = true;
        }
        
        // isWaitingResponse 해제하지 않음 - 응답 완료 후에는 자동으로 해제
        
        // 토큰 사용량 갱신
          try {
            const usageRes = await chatAPI.getTokenUsage();
            if (usageRes && usageRes.data) {
              this.tokenUsage = usageRes.data;
            }
          } catch (e) {
            console.error('[Token Usage] 토큰 사용량 갱신 오류', e);
          }

        // 입력창에 포커스
          this.$nextTick(() => {
            try {
              const chatInput = this.messages.length > 0 ? this.$refs.chatInputMain : this.$refs.chatInputEmpty;
              if (chatInput && chatInput.$refs && chatInput.$refs.messageInput) {
                chatInput.$refs.messageInput.focus();
              }
            } catch (focusError) {
              console.log('입력창 포커스 설정 중 오류:', focusError);
            }
          });
        
      } catch (error) {
        console.error('[Chat.vue] processServerResponse 에러:', error);
        this.alertMessage = '서버 응답 처리 중 오류가 발생했습니다.';
        this.showAlertModal = true;
      }
    },
    /**
     * SDK v1.2.0: 실제 스트림 처리 메서드
     * ReadableStream을 받아 실시간으로 메시지를 업데이트합니다.
     * @param {ReadableStream} stream - SDK에서 반환된 스트림
     * @param {number} assistantIdx - 어시스턴트 메시지 인덱스
     * @param {Object} userMessage - 원본 사용자 메시지 (PII 에러 시 재전송용)
     */
    async processRealStream(stream, assistantIdx, userMessage = null) {
      const reader = stream.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';
      let mergedMeta = {};

      this.isStreaming = true;
      this.isTyping = true;

      // PII 에러 코드 범위: 2100-2199
      const isPiiError = (code) => {
        const numCode = parseInt(code, 10);
        return numCode >= 2100 && numCode <= 2199;
      };

      try {
        // 단일 NDJSON 라인을 파싱하고 처리하는 내부 함수
        // 반환값: 'continue' (다음 청크), 'break' (for 루프 탈출), 'return' (메서드 즉시 종료)
        const processChunk = (raw) => {
          const line = raw.trim();
          if (!line || line.startsWith('event:') || line.startsWith('_meta:')) return 'continue';

          const jsonStr = line.startsWith('data:') ? line.slice(5).trim() : line;
          if (!jsonStr || jsonStr === '[DONE]') return 'continue';

          let chunk;
          try { chunk = JSON.parse(jsonStr); } catch { return 'continue'; }

          // _meta 누적
          if (chunk._meta) {
            mergedMeta = { ...mergedMeta, ...chunk._meta };
          }

          // 에러 처리
          if (chunk.error) {
            // chunk.error가 JSON 문자열인 경우 파싱하여 실제 메시지 추출
            let errorMessage = chunk.error;
            if (typeof errorMessage === 'string' && errorMessage.startsWith('{')) {
              try {
                const parsed = JSON.parse(errorMessage);
                errorMessage = parsed?.message?.content || parsed?.error || errorMessage;
              } catch (e) { /* 그대로 사용 */ }
            }
            let detail = chunk._meta?.detail;

            if (typeof detail === 'string') {
              try { detail = JSON.parse(detail); } catch (e) { /* 그대로 사용 */ }
            }

            // code는 _meta.code 또는 _meta.detail.code에 위치할 수 있음
            const errorCode = chunk._meta?.code ?? detail?.code;

            // predictions는 detail.predictions.detectedPii 또는 detail.predictions (배열)
            const piiItems = detail?.predictions?.detectedPii || (Array.isArray(detail?.predictions) ? detail.predictions : null);

            console.log('[processRealStream] 에러 청크:', { errorCode, errorMessage, detail, piiItems, hasUserMessage: !!userMessage });

            // PII 에러인 경우
            if (isPiiError(errorCode) && userMessage) {
              // predictions가 있으면 인라인 마스킹 모달 표시
              if (piiItems && piiItems.length > 0) {
                console.log('[processRealStream] PII 에러 감지, 인라인 마스킹 모달 표시');

                // 어시스턴트 메시지 제거 (에러이므로)
                this.messages.splice(assistantIdx, 1);

                // 마스킹된 텍스트 생성 + maskedContent 기준 오프셋 계산
                const predictions = piiItems;
                // originalStart 기준 정렬 (정방향)
                const sortedByStart = [...predictions].sort((a, b) => a.originalStart - b.originalStart);

                let maskedContent = '';
                let lastOrigIdx = 0;
                const adjustedPredictions = sortedByStart.map((pred) => {
                  // 원본에서 PII 앞의 일반 텍스트 추가
                  const plainText = userMessage.content.slice(lastOrigIdx, pred.originalStart);
                  maskedContent += plainText;

                  // maskedContent 기준 새 오프셋 기록
                  const newStart = maskedContent.length;
                  maskedContent += pred.maskedText;
                  const newEnd = maskedContent.length;

                  lastOrigIdx = pred.originalEnd;

                  return { ...pred, start: newStart, end: newEnd };
                });
                // 마지막 일반 텍스트 추가
                maskedContent += userMessage.content.slice(lastOrigIdx);

                // maskingState 설정 (인라인 UI용)
                this.maskingState = {
                  maskedUserContent: maskedContent,
                  userContent: userMessage.content,
                  detectedPii: adjustedPredictions,
                  masked: adjustedPredictions.map(() => true)
                };

                this.initializeMaskingParts();
                this.maskingUserMessage = userMessage;
                this.showMaskingModal = true;

                // 상태 초기화
                this.isStreaming = false;
                this.isTyping = false;
                this.isApiCalling = false;
                this.isWaitingResponse = false;
                return 'return';
              }

              // predictions 없는 PII 에러: detail 내용을 에러 메시지로 표시
              const displayMessage = (typeof detail === 'string' && detail)
                ? detail
                : errorMessage;
              this.$set(this.messages, assistantIdx, {
                ...this.messages[assistantIdx],
                content: displayMessage,
                isError: true,
                timestamp: Date.now()
              });
              return 'break';
            }

            // 일반 에러 메시지 표시
            this.$set(this.messages, assistantIdx, {
              ...this.messages[assistantIdx],
              content: errorMessage,
              isError: true,
              timestamp: Date.now()
            });
            return 'break';
          }

          // final 처리 (_meta.messageType === 'final')
          const messageType = chunk._meta?.messageType;
          if (messageType === 'final') {
            const finalContent = chunk.message?.content || accumulated;
            this.$set(this.messages, assistantIdx, {
              ...this.messages[assistantIdx],
              content: finalContent,
              timestamp: Date.now(),
              reference_docs: chunk.reference_docs || mergedMeta.reference_docs || []
            });
            return 'continue';
          }

          // system-notification 청크: RAG 검색 등 상태 알림은 임시 표시 후 실제 응답으로 교체
          if (chunk.model === 'system-notification' || messageType === 'notification' || messageType === 'system-notification') {
            const notifContent = chunk?.message?.content || '';
            if (notifContent && !accumulated) {
              // 아직 실제 응답이 없을 때만 임시 표시
              this.$set(this.messages, assistantIdx, {
                ...this.messages[assistantIdx],
                content: notifContent,
                isNotification: true
              });
              this.scrollToBottom();
            }
            return 'continue';
          }

          // normal 청크 - content 추출 (Ollama / OpenAI)
          const chunkContent =
            chunk?.message?.content ||
            chunk?.choices?.[0]?.delta?.content ||
            chunk?.delta ||
            '';

          if (chunkContent) {
            accumulated += chunkContent;
            this.$set(this.messages, assistantIdx, {
              ...this.messages[assistantIdx],
              isNotification: false,
              content: accumulated
            });
            this.scrollToBottom();
          }

          return 'continue';
        };

        let streamDone = false;
        while (!streamDone) {
          // 중지 상태 체크
          if (this.isPaused) {
            console.log('[processRealStream] 중지 상태로 스트림 읽기 중단');
            break;
          }

          const { done, value } = await reader.read();
          if (done) {
            console.log('[processRealStream] 스트림 완료');
            streamDone = true;
            break;
          }

          // SDK v1.2.1: Uint8Array → TextDecoder → NDJSON 라인 파싱
          // SDK가 string으로 enqueue하는 경우와 Uint8Array 모두 처리
          console.log('[processRealStream] read value:', { type: typeof value, constructor: value?.constructor?.name, preview: typeof value === 'string' ? value.slice(0, 200) : '(binary)' });
          if (typeof value === 'string') {
            buffer += value;
          } else {
            buffer += decoder.decode(value, { stream: true });
          }
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const raw of lines) {
            const result = processChunk(raw);
            if (result === 'return') return;
            if (result === 'break') break;
          }
        }

        // 스트림 종료 후 buffer에 남은 데이터 처리 (\n 없이 끝난 마지막 청크)
        if (buffer.trim()) {
          const result = processChunk(buffer);
          if (result === 'return') return;
          buffer = '';
        }

        // 스트림 완료 후 최종 상태 설정
        if (!this.isPaused) {
          // reference_docs 설정, notification 플래그 해제
          this.$set(this.messages, assistantIdx, {
            ...this.messages[assistantIdx],
            isNotification: false,
            timestamp: Date.now(),
            reference_docs: mergedMeta.reference_docs || this.messages[assistantIdx].reference_docs || []
          });

          // 토큰 사용량 갱신
          try {
            const usageRes = await chatAPI.getTokenUsage();
            if (usageRes && usageRes.data) {
              this.tokenUsage = usageRes.data;
            }
          } catch (e) {
            console.error('[processRealStream] 토큰 사용량 갱신 오류:', e);
          }
        }

      } catch (error) {
        console.error('[processRealStream] 스트림 처리 에러:', error);
        // 에러 발생 시 부분 콘텐츠라도 표시
        if (accumulated) {
          this.$set(this.messages, assistantIdx, {
            ...this.messages[assistantIdx],
            content: accumulated,
            timestamp: Date.now()
          });
        }
      } finally {
        reader.releaseLock();
        this.currentAbortController = null;
        this.isStreaming = false;
        this.isTyping = false;
        this.scrollToBottom();
      }
    },
    async handlePause() {
      console.log('[handlePause] 응답 중지 호출됨, 현재 상태:', {
        isPaused: this.isPaused,
        isTyping: this.isTyping,
        isStreaming: this.isStreaming,
        isWaitingResponse: this.isWaitingResponse,
        isApiCalling: this.isApiCalling,
        currentChat: this.currentChat?.sessionId
      });

      this.isPaused = true;
      this.isTyping = false;

      // SDK v1.2.1: AbortController로 fetch 요청 중단
      if ((this.isStreaming || this.isApiCalling) && this.currentAbortController) {
        this.isAborting = true;
        try {
          this.currentAbortController.abort('User cancelled');
          console.log('[handlePause] AbortController.abort() 호출 완료');
        } catch (e) {
          console.warn('[handlePause] AbortController abort 에러:', e);
        }
        this.currentAbortController = null;
        this.isAborting = false;
      }

      this.isStreaming = false;
      this.isApiCalling = false;

      if (this.currentResponseController) {
        this.currentResponseController.abort();
        this.currentResponseController = null;
      }
      this.isWaitingResponse = false;
      // 로딩(답변 작성 중) 메시지 즉시 제거
      this.messages = this.messages.filter(m => m.role !== 'loading');

      console.log('[handlePause] 응답 중지 완료, isPaused:', this.isPaused);
    },
    handleFileSelect(event) {
      this.attachedFiles = [...this.attachedFiles, ...event.target.files]
      event.target.value = null
    },
    removeFile(index) {
      this.attachedFiles.splice(index, 1)
    },
    toggleDebugMode() {
      this.debugMode = !this.debugMode;
      localStorage.setItem('debugMode', this.debugMode);
    },
    async selectModel(model) {
      try {
        const found = findModelById(model.id);
        if (!found) return;
        this.selectedModel = found;
        this.modelPlaceholder = found.name;

        // 현재 채팅방의 model 정보도 즉시 동기화 (임시/실제 구분 없이)
        if (this.currentChat) {
          this.currentChat.model = found.id;
        }

        // chatHistory에 동일 sessionId가 있으면 model 값을 갱신
        const idx = this.chatHistory.findIndex(c => c.sessionId === this.currentChat?.sessionId);
        if (idx !== -1) {
          this.$set(this.chatHistory[idx], 'model', found.id);
        }
      } catch (error) {
        console.error('모델 변경 중 오류 발생:', error);
      }
    },
    async logout() {
      // 로그아웃 요청을 기다리지 않고 바로 리다이렉트
      this.$router.push('/login')
      // 백그라운드에서 로그아웃 처리
      try {
        await authAPI.logout()
      } catch (error) {
        console.error('로그아웃 중 오류 발생:', error)
      }
    },
    /**
     * 채팅 히스토리를 서버로부터 로드하는 메서드
     * - 최근 30일간의 세션 요약 정보 조회
     * - 임시 채팅 상태 관리
     * - 화면 상태 업데이트
     */
    async loadChatHistory() {
      this.isChatHistoryLoading = true;
      try {
        const today   = new Date();
        const tomorrow = new Date(today);
        tomorrow.setUTCHours(24, 0, 0, 0);
        const endTime   = tomorrow.toISOString();
        const startTime = new Date(tomorrow.getTime() - 30*24*60*60*1000).toISOString();

        const summaries = await fetchSessionSummaries({
          userId: this.userId,
          startTime,
          endTime
        });

        const serverHistory = (summaries || [])
          .filter(item => !item.sessionId.startsWith('playground_'))
          .map(item => {
            // 기존 chatHistory에서 해당 sessionId의 model 정보 찾기
            const existingChat = (this.chatHistory || []).find(c => c.sessionId === item.sessionId);
            return {
            id: item.sessionId,
            sessionId: item.sessionId,
            title: item.content || '새 대화',
            created_at_ms: item.timestamp ? new Date(item.timestamp).getTime() : undefined,
            created_at: item.timestamp,
              model: existingChat?.model || null, // 기존 model 정보 보존
            messages: []
            };
          }).sort((a, b) => (b.created_at_ms || 0) - (a.created_at_ms || 0));

        // 임시 채팅 병합 유지
        const tempChats = (this.chatHistory || []).filter(
          c => c.isTemp && !serverHistory.some(s => s.sessionId === c.sessionId)
        );
        this.chatHistory = [...tempChats, ...serverHistory];
      } catch (error) {
        console.error('세션 요약 로드 중 오류 발생:', error);
      } finally {
        this.isChatHistoryLoading = false;
      }
    },
    async toggleHistoryPanel() {
      if (!this.showHistoryPanel) {
        // View All 진입: 쿼리스트링 추가
        await this.$router.push({ path: '/chat', query: { viewAll: '1' } });
        this.showHistoryPanel = true;
        // this.loadChatHistory(); // [불필요한 재로딩 제거: 메모리 목록 그대로 사용]
      } else {
        // View All 종료: 쿼리스트링 제거
        await this.$router.push({ path: '/chat' });
        this.showHistoryPanel = false;
      }
    },
    updateDebugMode(value) {
      this.debugMode = value;
      localStorage.setItem('debugMode', this.debugMode);
      // 디버그 모드 상태가 변경될 때마다 메시지를 다시 렌더링하기 위해
      this.messages = [...this.messages];
      this.encryptVisibleMessages();
    },
    // 스크롤을 맨 아래로 이동하는 메소드 추가
    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.chatContainer) {
          this.$refs.chatContainer.scrollTop = this.$refs.chatContainer.scrollHeight
        }
      })
    },
    updateInputAreaHeight() {
      this.$nextTick(() => {
        if (this.$refs.inputArea) {
          // 실제 채팅(메시지 있음)에서는 입력창 minHeight 48, 새 채팅(없음)에서는 120
          const minHeight = this.messages.length > 0 ? 48 : 120;
          this.inputAreaHeight = Math.max(this.$refs.inputArea.offsetHeight || minHeight, minHeight);
        } else {
          this.inputAreaHeight = this.messages.length > 0 ? 48 : 120;
        }
      });
    },
    /**
     * 채팅 입력창 크기 변경을 처리하는 메서드
     * - 스크롤 위치 보정
     * - 입력창 높이 조정
     */
    handleInputResize() {
      const el = this.$refs.chatContainer;
      if (!el) return;
      // 입력창 크기 변화 전의 상태 저장
      const prevScrollHeight = el.scrollHeight;
      const prevScrollTop = el.scrollTop;
      const prevClientHeight = el.clientHeight;
      const wasAtBottom = Math.abs(prevScrollHeight - prevScrollTop - prevClientHeight) < 10;
      this.wasAtBottom = wasAtBottom;
      this.prevScrollHeight = prevScrollHeight;
      this.prevScrollTop = prevScrollTop;
      this.prevClientHeight = prevClientHeight;
      this.$nextTick(() => {
        if (this.wasAtBottom) {
          el.scrollTop = el.scrollHeight;
        } else {
          // scrollHeight, clientHeight 변화량을 모두 반영하여 scrollTop 보정
          const deltaScrollHeight = el.scrollHeight - this.prevScrollHeight;
          const deltaClientHeight = el.clientHeight - this.prevClientHeight;
          el.scrollTop = this.prevScrollTop + deltaScrollHeight - deltaClientHeight;
        }
      });
    },
    /**
     * 디버그 모드에서 현재 화면에 보이는 메시지들의 암호화를 처리하는 메서드
     * - Viewport 내에 있는 메시지만 선택적으로 처리
     * - 스크롤 시 새로 보이는 메시지들도 처리
     * - 성능 최적화를 위한 디바운스 처리 포함
     */
    async encryptVisibleMessages() {
      if (!this.debugMode || !this.$sdk) return;
      
      // 채팅 컨테이너 element 가져오기
      const container = this.$refs.chatContainer;
      if (!container) return;
      
      // 현재 화면에 보이는 메시지들 찾기
      const messageElements = container.querySelectorAll('.message-item');
      const visibleMessages = Array.from(messageElements).filter(el => {
        const rect = el.getBoundingClientRect();
        return rect.top < window.innerHeight && rect.bottom >= 0;
      });
      
      // 보이는 메시지들 암호화
      for (const messageEl of visibleMessages) {
        const messageId = messageEl.dataset.messageId;
        const message = this.messages.find(m => m.id === messageId);
        if (!message || !message.content || message.encryptedContent) continue;
        
        try {
          message.encryptedContent = await encryptMessage(message.content, this.$sdk);
        } catch (e) {
          message.encryptedContent = '[암호화 실패]';
        }
      }
      
      // 변경 알림을 위한 배열 복사
      this.messages = [...this.messages];
    },

    // 스크롤 이벤트 핸들러 (디바운스 처리)
    handleScrollDebounced: debounce(function() {
      if (this.debugMode) {
        this.encryptVisibleMessages();
      }
    }, 200),

    saveTempAssistantMessage(sessionId, assistantMsg) {
      const cacheKey = 'chat_temp_cache';
      let cache = {};
      try {
        cache = JSON.parse(localStorage.getItem(cacheKey)) || {};
      } catch (e) {
        cache = {};
      }
      if (!cache[sessionId]) cache[sessionId] = [];
      // 중복 방지: id 기준
      if (!cache[sessionId].some(m => m.id === assistantMsg.id)) {
        cache[sessionId].push(assistantMsg);
        localStorage.setItem(cacheKey, JSON.stringify(cache));
      }
    },
    removeTempAssistantMessages(sessionId, serverMessages) {
      const cacheKey = 'chat_temp_cache';
      let cache = {};
      try {
        cache = JSON.parse(localStorage.getItem(cacheKey)) || {};
      } catch (e) {
        cache = {};
      }
      if (!cache[sessionId]) return;
      // 서버 메시지와 content가 완전히 같은 임시 메시지는 삭제
      cache[sessionId] = cache[sessionId].filter(tempMsg => {
        return !serverMessages.some(serverMsg =>
          serverMsg.role === 'assistant' &&
          tempMsg.role === 'assistant' &&
          serverMsg.content && tempMsg.content &&
          serverMsg.content.trim() === tempMsg.content.trim()
        );
      });
      if (cache[sessionId].length === 0) {
        delete cache[sessionId];
      }
      localStorage.setItem(cacheKey, JSON.stringify(cache));
    },
    getTempAssistantMessages(sessionId) {
      const cacheKey = 'chat_temp_cache';
      let cache = {};
      try {
        cache = JSON.parse(localStorage.getItem(cacheKey)) || {};
      } catch (e) {
        cache = {};
      }
      return cache[sessionId] || [];
    },
    // SDK 체크 공통 메서드
    checkSDK() {
      if (!this.$sdk) {
        console.error('[Chat.vue] SDK가 아직 초기화되지 않았습니다.');
        this.alertMessage = 'SDK 초기화 중입니다. 잠시 후 다시 시도해주세요.';
        this.showAlertModal = true;
        return false;
      }
      return true;
    },
    // 메시지 입력 박스에 포커스를 설정하는 메서드
    focusMessageInput() {
      try {
        const chatInput = this.messages.length > 0 ? this.$refs.chatInputMain : this.$refs.chatInputEmpty;
        if (chatInput && chatInput.$refs && chatInput.$refs.messageInput) {
          chatInput.$refs.messageInput.focus();
        }
      } catch (focusError) {
        console.log('입력창 포커스 설정 중 오류:', focusError);
      }
    },
  },
  async mounted() {
    // 사용자 정보 세팅 (View All 분기 전에 반드시 실행)
    const userInfo = JSON.parse(localStorage.getItem('userInfo'))
    if (userInfo) {
      this.userName = userInfo.name || ''
      this.userEmail = userInfo.email || ''
      this.userId = userInfo.userId || userInfo.email || ''

      // ✅ 기본 SDK로 복구 (Role Playground에서 오염되었을 가능성 대비)
              try {
          // SDK는 main.js에서 Vue.prototype.$sdk로 설정됨
          // fallback/재초기화 로직 제거 (main.js에서만 초기화)
        } catch (e) {
        console.error('[Chat] SDK 복구 중 오류:', e);
      }
    } else {
      if (this.$route.path !== '/login') {
        this.$router.push('/login');
      }
      return
    }
    // force-new-chat 이벤트 리스너 등록
    window.addEventListener('force-new-chat', this.createNewChat)
    if (this.$route.query.new === '1') {
      await this.createNewChat();
      this.$router.replace({ path: '/chat' });
      return;
    }
    // View All(전체보기) 쿼리 분기: 새로고침/라우터 진입 시에도 항상 유지
    if (this.$route.query.viewAll === '1') {
      this.showHistoryPanel = true;
      // View All 상태에서도 히스토리 반드시 로드
      this.loadChatHistory(); // [중복 호출 방지] mounted()에서만 호출하도록 주석 처리
      return;
    }
    if (this.$route.query.viewAll === '1' || this.showHistoryPanel) return;
    try {
      // 채팅 히스토리와 토큰 사용량을 동시에 로드
      await Promise.all([
        this.loadChatHistory(),
        (async () => {
          try {
            
            const usageRes = await chatAPI.getTokenUsage();
            if (usageRes && usageRes.data) {
              this.tokenUsage = usageRes.data;
            }
          } catch (e) {
            // 에러 무시
          }
        })()
      ])

      // 마지막으로 본 채팅방 sessionId가 있으면 해당 채팅방을 자동으로 불러오고, 없으면 새 채팅 생성
      const lastSessionId = localStorage.getItem('lastSessionId');
      if (lastSessionId && this.chatHistory.some(c => c.sessionId === lastSessionId)) {
        await this.loadChat(lastSessionId);
      } else {
        await this.createNewChat();
      }
      if (this.messages.length === 0) {
        this.isModelSelectionDisabled = false;
      }
      this.updateInputAreaHeight();
      window.addEventListener('resize', this.updateInputAreaHeight);
      await this.encryptVisibleMessages();
    } catch (error) {
      // 데이터 조회 중 오류 발생 시 콘솔에만 출력
      // console.error('데이터 조회 중 오류 발생:', error)
    }

    // 스크롤 이벤트 리스너 등록
    if (this.$refs.chatContainer) {
      this.$refs.chatContainer.addEventListener('scroll', this.handleScrollDebounced);
    }

    // 페이지 로드 시 메시지 입력 박스에 자동 포커스
    this.$nextTick(() => {
      this.focusMessageInput();
    });
  },
  beforeDestroy() {
    // 스크롤 이벤트 리스너 제거
    if (this.$refs.chatContainer) {
      this.$refs.chatContainer.removeEventListener('scroll', this.handleScrollDebounced);
    }
  },
  watch: {
    '$route.path': {
      handler(newPath) {
        if (this.$route.query.new === '1') {
          this.createNewChat();
          this.$router.replace({ path: '/chat' });
          return;
        }
        if (this.$route.query.viewAll === '1' || this.showHistoryPanel) return;
        if (newPath === '/chat' && (!this.currentChat || this.messages.length === 0)) {
          this.currentChat = null
          this.messages = []
          this.showHistoryPanel = false
        }
      }
    },
    // View All 쿼리 변화 감시: 항상 showHistoryPanel과 동기화
    '$route.query.viewAll'(val) {
      this.showHistoryPanel = val === '1';
    },
    // 채팅방 변경 감지
    currentChat: {
      handler(newChat, oldChat) {
        console.log('[currentChat watcher] 채팅방 변경 감지:', {
          oldSessionId: oldChat?.sessionId,
          newSessionId: newChat?.sessionId,
          isWaitingResponse: this.isWaitingResponse,
          isTyping: this.isTyping,
          isApiCalling: this.isApiCalling
        });
        
        if (oldChat && newChat?.sessionId !== oldChat?.sessionId) {
          if (this.isWaitingResponse || this.isTyping || this.isApiCalling) {
            console.log('[currentChat watcher] 응답 중이므로 handlePause() 호출');
            this.handlePause();
          }
        }
      },
      immediate: false
    },
    messages() {
      this.updateInputAreaHeight();
    },
    isWaitingResponse() {
      this.updateInputAreaHeight();
    },
    // 디버그 모드 변경 감지
    debugMode(newValue) {
      if (newValue) {
        this.encryptVisibleMessages();
      }
    }
  }
}
</script>

<style scoped>
/* 마스킹 모달 체크박스 스타일 */
.masking-checkbox {
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  width: 16px !important;
  height: 16px !important;
  border: 1px solid #ccc !important;
  border-radius: 3px !important;
  background: white !important;
  outline: none !important;
  box-shadow: none !important;
  cursor: pointer !important;
  position: relative !important;
  display: inline-block !important;
}

.masking-checkbox:checked {
  background: white !important;
  border-color: black !important;
}

.masking-checkbox:checked::after {
  content: '✓' !important;
  color: black !important;
  font-size: 14px !important;
  position: absolute !important;
  top: -1px !important;
  left: 2px !important;
  line-height: 1 !important;
}

.masking-checkbox:focus,
.masking-checkbox:focus-visible,
.masking-checkbox:active,
.masking-checkbox:hover {
  outline: none !important;
  box-shadow: none !important;
}
</style> 